import React, { useState } from 'react';
import { Editor } from 'react-draft-wysiwyg';

import { EditorState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import PptxGenJS from 'pptxgenjs';



import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';


const PowerPointEditor = () => {
  const [sections, setSections] = useState([
    {
      title: 'Template Examples: Your Topic',
      subtitle: 'Write a summary for your custom case.',
      examples: [
        {
          label: 'Example 1',
          editorState: EditorState.createEmpty(),
          fontSize: 14,
          fontColor: '#000000'
        },
      ],
    },
  ]);

  const handleSectionChange = (index, key, value) => {
    const updated = [...sections];
    updated[index][key] = value;
    setSections(updated);
  };

  const handleExampleChange = (sectionIndex, exampleIndex, key, value) => {
    const updated = [...sections];
    updated[sectionIndex].examples[exampleIndex][key] = value;
    setSections(updated);
  };

  const addExample = (sectionIndex) => {
    const updated = [...sections];
    updated[sectionIndex].examples.push({
      label: '',
      editorState: EditorState.createEmpty(),
      fontSize: 14,
      fontColor: '#000000',
    });
    setSections(updated);
  };

  const removeExample = (sectionIndex, exampleIndex) => {
    const updated = [...sections];
    updated[sectionIndex].examples = updated[sectionIndex].examples.filter(
      (_, i) => i !== exampleIndex
    );
    setSections(updated);
  };

  const addSection = () => {
    setSections([
      ...sections,
      {
        title: 'New Section Title',
        subtitle: 'New section subtitle',
        examples: [
          {
            label: '',
            editorState: EditorState.createEmpty(),
            fontSize: 14,
            fontColor: '#000000',
          },
        ],
      },
    ]);
  };

  const removeSection = (index) => {
    setSections(sections.filter((_, i) => i !== index));
  };

  const convertHtmlToPptxText = (html, fontSize, color) => {
    const tempEl = document.createElement('div');
    tempEl.innerHTML = html;

    const paragraphs = tempEl.querySelectorAll('p, ul, ol');
    const lines = [];

    paragraphs.forEach((el) => {
      const isList = el.tagName === 'UL' || el.tagName === 'OL';
      if (isList) {
        const items = el.querySelectorAll('li');
        items.forEach((li) => {
          lines.push({
            text: '• ' + li.textContent,
            options: { fontSize, color },
          });
        });
      } else {
        lines.push({
          text: el.textContent,
          options: { fontSize, color },
        });
      }
    });

    return lines;
  };

  const handleExport = () => {
    const pptx = new PptxGenJS();
    const maxExamplesPerSlide = 2;

    sections.forEach(({ title, subtitle, examples }) => {
      for (let i = 0; i < examples.length; i += maxExamplesPerSlide) {
        const slide = pptx.addSlide();
        const chunk = examples.slice(i, i + maxExamplesPerSlide);

        slide.addText(title, {
          x: 0.5, y: 0.3, w: '90%', h: 0.6,
          fontSize: 20, bold: true, color: 'FFFFFF',
          fill: { color: '4B0082' }, align: 'center'
        });

        slide.addText(subtitle, {
          x: 0.5, y: 1.0, w: '90%',
          fontSize: 16, color: '5A2A7E', align: 'center'
        });

        let y = 1.8;
        chunk.forEach(({ label, editorState, fontSize, fontColor }) => {
          const rawContent = convertToRaw(editorState.getCurrentContent());
          const html = draftToHtml(rawContent);
          const textLines = convertHtmlToPptxText(html, fontSize, fontColor);

          slide.addText(label || 'Example', {
            x: 0.5, y, w: 2, h: 0.5,
            fontSize: 14, color: 'FFFFFF', bold: true,
            fill: { color: '333333' }, align: 'center'
          });

          slide.addText(textLines, {
            x: 0.5, y: y + 0.5, w: '90%', h: 1.5,
            margin: 0.1
          });

          y += 2.5;
        });

        slide.addText('Generated via Custom Slide Editor', {
          x: 0, y: 6.8, w: '100%',
          fontSize: 10, color: '888888', align: 'center'
        });
      }
    });

    pptx.writeFile('CustomSlides');
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-8">
      <h1 className="text-2xl font-bold text-purple-800">Create Slide Content</h1>

      {sections.map((section, sectionIndex) => (
        <div key={sectionIndex} className="border rounded p-4 bg-gray-100 space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-purple-700">Section {sectionIndex + 1}</h2>
            <button
              onClick={() => removeSection(sectionIndex)}
              className="text-red-600 hover:text-red-800"
            >
              Remove Section
            </button>
          </div>

          <div>
            <label className="font-semibold">Title:</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2 mt-1"
              value={section.title}
              onChange={(e) => handleSectionChange(sectionIndex, 'title', e.target.value)}
            />
          </div>

          <div>
            <label className="font-semibold">Subtitle:</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2 mt-1"
              value={section.subtitle}
              onChange={(e) => handleSectionChange(sectionIndex, 'subtitle', e.target.value)}
            />
          </div>

          <div>
            <label className="font-semibold">Examples:</label>
            {section.examples.map((example, exampleIndex) => (
              <div key={exampleIndex} className="border p-3 rounded mb-3 bg-white">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold">Block {exampleIndex + 1}</span>
                  <button
                    onClick={() => removeExample(sectionIndex, exampleIndex)}
                    className="text-red-500 hover:text-red-700"
                  >
                    ✕
                  </button>
                </div>
                <input
                  type="text"
                  className="w-full border rounded px-3 py-2 mb-2"
                  placeholder="Label (e.g., Insight, Note)"
                  value={example.label}
                  onChange={(e) => handleExampleChange(sectionIndex, exampleIndex, 'label', e.target.value)}
                />
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700">Font Size</label>
                  <input
                    type="number"
                    min="10"
                    max="40"
                    value={example.fontSize}
                    onChange={(e) => handleExampleChange(sectionIndex, exampleIndex, 'fontSize', parseInt(e.target.value))}
                    className="border rounded px-2 py-1 w-24"
                  />
                </div>
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700">Font Color</label>
                  <input
                    type="color"
                    value={example.fontColor}
                    onChange={(e) => handleExampleChange(sectionIndex, exampleIndex, 'fontColor', e.target.value)}
                  />
                </div>
                <Editor
                  editorState={example.editorState}
                  toolbar={{ options: ['inline', 'list'] }}
                  wrapperClassName="demo-wrapper"
                  editorClassName="demo-editor border min-h-[150px] p-2"
                  onEditorStateChange={(editorState) => handleExampleChange(sectionIndex, exampleIndex, 'editorState', editorState)}
                />
              </div>
            ))}
            <button
              onClick={() => addExample(sectionIndex)}
              className="mt-2 text-sm text-blue-600 hover:underline"
            >
              + Add Block
            </button>
          </div>
        </div>
      ))}

      <button
        onClick={addSection}
        className="text-purple-700 hover:text-purple-900 font-semibold py-2 px-4 rounded border border-purple-700"
      >
        + Add New Section
      </button>

      <button
        onClick={handleExport}
        className="bg-purple-700 hover:bg-purple-800 text-white font-bold py-2 px-4 rounded"
      >
        Export to PowerPoint
      </button>
    </div>
  );
};

export default PowerPointEditor;
